/* eslint-disable max-lines */
/* eslint-disable jsdoc/require-jsdoc */
import { useEffect, useState } from 'react';
import _ from 'lodash';
import LabelsList from 'components/LabelsList';
import MultiSearchableMenus from 'components/FilterMenus/MultiSearchableMenus/MultiSearchableMenus';
import { useDebounceCallback } from '@react-hook/debounce';
import { TRACKING } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import { camelCaseToTitleCase } from 'common/utils/utils';
import { LineSkeleton } from '../base/Skeletons';
import { SUPPORTED_PM_FILTERS } from './pm.filters';
import { useSelector } from 'react-redux';
import { pmIntegrationSelector } from 'state/selectors/sensors-authorizations';
import {
  getNoDataMessage,
  DEFAULT_SUPPORTED_FIELDS,
  filterTypesMapper,
  getAllCustomFields
} from './FilterMenus.service';
import { FILTER_TYPES } from 'common/constants/filters';
import useFetchEntities from './useFetchEntities';
import useEntitiesList from './useEntitiesList';
import useMainMenu from './useMainMenu';
import useEntitiesMenu from './useEntitiesMenu';
import useFreeInputMenu from './useFreeInputMenu';

const COMMON_FILTER_TYPES = _.values(FILTER_TYPES);

const formatFilterTypeToLabel = (key, values, label) => ({
  key,
  label,
  values
});

const formatSelectedFiltersToLabelsList = (
  selectedFilters,
  customFieldsMapper = {}
) => {
  const omitFromCommonTypes = [FILTER_TYPES.DYNAMIC_ISSUES];

  const commonFilterTypes = _.omit(
    _.pick(selectedFilters, COMMON_FILTER_TYPES),
    omitFromCommonTypes
  );
  const customFilterTypes = _.omit(selectedFilters, COMMON_FILTER_TYPES);
  const commonFilterTypesLabels = _.map(commonFilterTypes, (values, key) =>
    formatFilterTypeToLabel(
      key,
      values,
      _.get(customFieldsMapper, key, camelCaseToTitleCase(key))
    )
  );
  const customFilterTypesLabels = _.map(customFilterTypes, (values, key) =>
    formatFilterTypeToLabel(
      key,
      values,
      _.get(customFieldsMapper, key, camelCaseToTitleCase(key))
    )
  );
  const customFilterTypesLabelsGroup = {
    isGroup: true,
    groupItems: customFilterTypesLabels
  };

  const dynamicIssues = selectedFilters[FILTER_TYPES.DYNAMIC_ISSUES];
  const dynamicIssuesByEntityType = _.groupBy(dynamicIssues, 'entity_type');
  const dynamicIssuesLabels = _.map(
    dynamicIssuesByEntityType,
    (issues, issueType) =>
      formatFilterTypeToLabel(FILTER_TYPES.DYNAMIC_ISSUES, issues, issueType)
  );

  return [
    ...commonFilterTypesLabels,
    customFilterTypesLabelsGroup,
    ...dynamicIssuesLabels
  ];
};

// eslint-disable-next-line max-lines-per-function
const FilterMenusV2 = ({
  menuItems = SUPPORTED_PM_FILTERS,
  viewOnly = false, // boolean. send true to make all FilterMenusV2 disbaled
  supportAllFields = false,
  selectedFilters,
  setSelectedFilters,
  isLoading,
  AddFilterTrackingProps,
  SelectFilterTrackingProps,
  onDeleteFilter = null,
  buttonTitle = 'Add Filter',
  buttonIcon = 'Add',
  documentIdPrefix = null,
  onClearFilters,
  btnClearFiltersTitle,
  customFieldsMapper,
  withEntityPayload = false, // will return {id, title, entity(according to type to keep the original object)}
  withMargin = true, // Add margin-right to 'Add Filter' button
  withOperators = true, // show 'AND' 'OR' operators in LabelsList
  autoResponsive = true,
  triggerMenuButtonRef = null,
  children
}) => {
  const pmSensor = useSelector(pmIntegrationSelector) || {};
  const pmProvider = pmSensor?.sensor_id;
  const providerMenutItems = Object.values(menuItems[pmProvider] || {});
  const [customFieldsMapperState, setCustomFieldsMapperState] =
    useState(customFieldsMapper);
  const [selectedField, setSelectedField] = useState(null);
  const [noDataMessage, setNoDataMessage] = useState('');

  const isSingleFilterMode = providerMenutItems?.length === 1;

  const {
    mainMenu,
    customFetchProps,
    selectedFilterType,
    setSelectedFilterType
  } = useMainMenu({
    menuItems: providerMenutItems,
    setSelectedField,
    isSingleFilterMode
  });

  const currentMenuItem = providerMenutItems.find(
    ({ id }) => id === selectedFilterType
  );

  const {
    entities,
    isLoading: loadingData,
    fetchEntities,
    shouldShowMoreItem,
    onInitPagination,
    showMoreEntities,
    keyword,
    setKeyword
  } = useFetchEntities({
    currentMenuItem,
    supportAllFields,
    customFetchProps
  });

  const {
    listData: filterTypeData,
    selectedCheckboxes,
    setSelectedCheckboxes
  } = useEntitiesList({
    currentMenuItem,
    selectedFilters,
    entities,
    selectedField,
    setSelectedField,
    shouldShowMoreItem,
    showMoreEntities,
    keyword,
    selectedFilterType
  });

  const onCloseMenu = level => {
    setSelectedField(null);
    if (level < 3) {
      setSelectedFilterType(null);
      setKeyword('');
      onInitPagination();
      setSelectedCheckboxes([]);
    }
  };

  const { entitiesMenu } = useEntitiesMenu({
    entities: filterTypeData,
    currentMenuItem,
    onCloseMenu,
    setKeyword,
    selectedCheckboxes,
    noDataMessage,
    withEntityPayload,
    selectedFilters,
    setSelectedFilters
  });

  const { freeInputMenu } = useFreeInputMenu({
    selectedField,
    selectedFilters,
    setSelectedField,
    setSelectedFilterType,
    onCloseMenu,
    setSelectedFilters
  });

  const defaultOnDelteFilter = (filterType, id) => {
    const currentSelectedFilters = _.get(selectedFilters, filterType, []);
    const newSelectedFilters = currentSelectedFilters.filter(
      item => item.id !== id
    );
    const filtersAfterDelete = {
      ..._.omit(selectedFilters, filterType),
      ...(newSelectedFilters.length ? { [filterType]: newSelectedFilters } : {})
    };
    setSelectedFilters(filtersAfterDelete);
  };

  const handleDeleteFilter = onDeleteFilter || defaultOnDelteFilter;

  useEffect(() => {
    const commonFilterTypesMapper = filterTypesMapper[pmSensor?.sensor_id];

    if (!customFieldsMapper || _.isEmpty(customFieldsMapper)) {
      const fetchAllCustomFields = async () => {
        const allCustomFields = await getAllCustomFields();
        const customFieldsMapper = allCustomFields?.reduce((acc, field) => {
          acc[field.entity_id] = field.value;
          return acc;
        }, {});
        setCustomFieldsMapperState({
          ...commonFilterTypesMapper,
          ...customFieldsMapper
        });
      };
      fetchAllCustomFields();
    } else {
      setCustomFieldsMapperState({
        ...commonFilterTypesMapper,
        ...customFieldsMapper
      });
    }
  }, []);

  const onSearchFilterValueChange = useDebounceCallback(async () => {
    onInitPagination();
    fetchEntities({ keyword });
  }, 700);

  useEffect(() => {
    async function handle() {
      if (selectedFilterType) {
        const filterData = await fetchEntities();
        TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
          ...SelectFilterTrackingProps,
          field_name: selectedFilterType
        });
        if (!filterData?.length) {
          const noDataMessage = await getNoDataMessage(selectedFilterType);
          // @ts-ignore
          setNoDataMessage(noDataMessage);
        }
      }
    }

    handle();
  }, [selectedFilterType]);

  useEffect(() => {
    onSearchFilterValueChange();
  }, [keyword]);

  const menus = [mainMenu, entitiesMenu, freeInputMenu];

  const showSkeleton =
    isLoading ||
    (_.isEmpty(customFieldsMapperState) &&
      Object.keys(DEFAULT_SUPPORTED_FIELDS).includes(pmProvider?.toString()));

  if (showSkeleton) {
    return (
      <div className='w-40'>
        <LineSkeleton />
      </div>
    );
  }

  const onClickTriggerButton = () => {
    if (isSingleFilterMode) {
      setSelectedFilterType(providerMenutItems?.[0]?.id);
    }
  };

  return (
    <div
      className='inline-flex flex-row items-center flex-wrap gap-2 align-start'
      data-testid='labels-flex-container'
    >
      <LabelsList
        list={formatSelectedFiltersToLabelsList(
          selectedFilters,
          customFieldsMapperState
        )}
        onDelete={!viewOnly ? handleDeleteFilter : null}
        withOperators={withOperators}
      />
      <MultiSearchableMenus
        AddFilterTrackingProps={AddFilterTrackingProps}
        buttonTitle={buttonTitle}
        buttonIcon={buttonIcon}
        menus={menus}
        searchFilterValue={keyword}
        setSearchFilterValue={setKeyword}
        documentIdPrefix={documentIdPrefix}
        isLoading={loadingData}
        onClearFilters={onClearFilters}
        btnClearFiltersTitle={btnClearFiltersTitle}
        withMargin={withMargin}
        autoResponsive={autoResponsive}
        viewOnly={viewOnly}
        onCloseMenu={onCloseMenu}
        triggerMenuButtonRef={triggerMenuButtonRef}
        isSingleFilterMode={isSingleFilterMode}
        onClickTriggerButton={onClickTriggerButton}
      />
      {children}
    </div>
  );
};

export default FilterMenusV2;
